import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { MerchantDto } from "@/features/merchant/api/dto";
import { MerchantRowActions } from "@/features/merchant/components/merchants/merchant-row-actions";
import {
  MERCHANT_STATUSES,
  MERCHANT_TYPES,
  type MerchantStatus,
} from "@/features/shared/api/enums";

/** Ligne de la liste : `DataTableClar` exige un `id` requis (optionnel dans le DTO). */
export type MerchantRow = MerchantDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  MerchantStatus,
  "warning" | "success" | "destructive" | "secondary"
> = {
  PENDING: "warning",
  ACTIVE: "success",
  SUSPENDED: "destructive",
  CLOSED: "secondary",
};

/**
 * Colonnes de la liste des marchands (`POST /Merchants/all`).
 * Filtres select sur les énumérations (annexe A) ; texte sur
 * `tradeName` et `ownerPartyId` (champs `MerchantFilter`) ; identifiants
 * et données financières en `NoTranslate` (règle notranslate).
 */
export const merchantsColumns: ColumnsClarFactory<MerchantRow> = (
  navigate
) => [
  {
    id: "merchantNumber",
    accessorKey: "merchantNumber",
    header: t("merchant.merchants.columns.merchantNumber"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-medium">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "legalName",
    accessorKey: "legalName",
    header: t("merchant.merchants.columns.legalName"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "tradeName",
    accessorKey: "tradeName",
    header: t("merchant.merchants.columns.tradeName"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">{(getValue() as string) || "—"}</NoTranslate>
    ),
    meta: {
      type: "text",
      placeholder: t("merchant.merchants.filters.tradeNamePlaceholder"),
    },
  },
  {
    id: "categoryCode",
    accessorKey: "categoryCode",
    header: t("merchant.merchants.columns.categoryCode"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{(getValue() as string) || "—"}</Badge>
      </NoTranslate>
    ),
    meta: { type: "denied" },
  },
  {
    id: "merchantType",
    accessorKey: "merchantType",
    header: t("merchant.merchants.columns.merchantType"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="secondary">{(getValue() as string) || "—"}</Badge>
      </NoTranslate>
    ),
    meta: { type: "select", options: selectOptions(MERCHANT_TYPES) },
  },
  {
    id: "status",
    accessorKey: "status",
    header: t("merchant.merchants.columns.status"),
    cell: ({ getValue }) => {
      const status = getValue() as MerchantStatus | undefined;
      return status ? (
        <NoTranslate as="span">
          <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>{status}</Badge>
        </NoTranslate>
      ) : (
        "—"
      );
    },
    meta: { type: "select", options: selectOptions(MERCHANT_STATUSES) },
  },
  {
    id: "kycStatus",
    accessorKey: "kycStatus",
    header: t("merchant.merchants.columns.kycStatus"),
    cell: ({ getValue }) => (
      <NoTranslate as="span">
        <Badge variant="outline">{(getValue() as string) || "—"}</Badge>
      </NoTranslate>
    ),
    // Pas dans MerchantFilter.
    meta: { type: "denied" },
  },
  {
    id: "ownerPartyId",
    accessorKey: "ownerPartyId",
    header: t("merchant.merchants.columns.ownerPartyId"),
    cell: ({ getValue }) => (
      <NoTranslate as="span" className="font-mono text-xs">
        {(getValue() as string) || "—"}
      </NoTranslate>
    ),
    meta: { type: "text" },
  },
  {
    id: "actions",
    header: t("common.actions"),
    enableHiding: false,
    cell: ({ row }) =>
      row.original.id ? (
        <MerchantRowActions merchant={row.original} navigate={navigate} />
      ) : null,
    meta: { type: "denied" },
  },
];
